import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import SideBar from 'features/side-bar';
import { EVBox, EVTypography } from '@eagleview/ev-comp-library';
import { isAuthorized, setLocalStorage } from 'utils/app.utils';
import MfaCommunicator from 'utils/mfa.utils';
import ErrorBoundary from 'commonComponents/ErrorBoundary';
import Header from 'commonComponents/Header';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actionCreators from 'features/side-bar/SideBar.actions';
import PropTypes from 'prop-types';
import {
  IS_NOTIFS_TRANSPORT_SERVICE_ENABLED, FULL_SCREEN_PATHS, SIGN_IN_AS_GUID,
  ESRI_PKCE_TOKEN_KEY,
} from 'constants/AppConstants';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import EagleViewSupportLink from 'commonComponents/EagleViewSupportLink/EagleViewSupportLink.component';
import FullscreenToggleButton from 'commonComponents/FullscreenToggleButton/FullscreenToggleButton.component';
import * as authorizeActions from 'features/authorize/Authorize.actions';
import SharedStartupGate from 'features/shared-startup/SharedStartupGate.component';
import Interceptor from './fetchInterceptor';
import Routes from './routes';
import { store } from './store/ConfigureStore';
import { ACCESS_PHASES } from './constants/GuestAccess';

// register fetch interceptor
Interceptor().register();

// ─── Public routes ────────────────────────────────────────────────────────────
// These paths render without the app chrome (no header / sidebar / SharedStartupGate).
// IMPORTANT: keep in sync with the UNGUARDED_PATHS list in public/index.html so
// the pre-React Okta redirect script also skips them. /authorize/* (e.g. callback)
// must skip the gate so Okta redirect doesn't trigger guest/shared-resource APIs. #genai
const PUBLIC_PATHS = ['/logout-handler', '/signin', '/verify'];
const isAuthorizePath = (pathname) => (pathname || '').startsWith('/authorize');

// expose methods for micro apps.
MfaCommunicator({ store });

// Shared ErrorBoundary fallback — shown whenever an uncaught render error occurs.
// Defined here (module level) so it isn't recreated on every App render.
const AppErrorFallback = (t) => () => (
  <EVBox
    width="70%"
    margin="auto"
    height="100%"
    position="absolute"
    display="flex"
    justifyContent="flex-start"
    alignItems="center"
    flexDirection="column"
  >
    <EVTypography>
      {t('EncounteredAnError')}
      <EagleViewSupportLink />
    </EVTypography>
  </EVBox>
);

window.onstorage = (event) => {
  if (event.key === 'token' && event.newValue && event.oldValue !== event.newValue) {
    sessionStorage.setItem('token', localStorage.getItem('token'));
  }
};

/**
 * App Component - renders essential components like
 * Provider - to inject redux store
 * EVThemeProvider - to inject Metarial UI
 * Router - to inject React-Router
 */
const App = (props) => {
  // The following piece of code was added for solving a bug related to LATLONG-4850: ESRI Login
  // When user tries to log into his/her esri account from LA, the login page opens in a separate popup window
  // after login is success, the page will be redirected to viewpoint url with access_token added as a parameter to the url

  const params = new URLSearchParams(window.location.search);

  const showBlankPage = params.get('showBlankPage');
  if (showBlankPage) {
    const authCode = params.get('code');
    if (authCode) {
      window.localStorage.setItem(
        ESRI_PKCE_TOKEN_KEY,
        JSON.stringify({
          code: authCode,
          at: Date.now(),
        }),
      );
    }
    window.close();
  }
  const { t } = useTranslation();
  const {
    authorize, userDetails, featureFlagsLoaded, flags, showNewUI, shouldHeaderRender, userProfileError,
    sharedPhase, isResourceShared, sharedIsEVUser, sharedHasAccess, sharedAppCode, isShareToken,
    actions: {
      getMfaConfigAction, getUserDetails, getGuestUserDetails, getLoaders, setShowNewUI, setEvoneLdShowBanner,
      toggleFullscreenAction, validateUserImpersonation, fetchSharedAppConfig,
    }, mfaList,
  } = props;

  const dispatch = useDispatch();

  /**
   * if user is authorized, dispatches action to fetch entitled applications
   */
  const getMfaConfigAndUser = () => {
    if (authorize) {
      setLocalStorage();
      if (FULL_SCREEN_PATHS.includes(window.location.pathname)) {
        toggleFullscreenAction({ hideSidebar: true, hideHeader: true });
      }
      const signInAsGuid = localStorage.getItem(SIGN_IN_AS_GUID);
      if (signInAsGuid) {
        validateUserImpersonation(signInAsGuid);
        return;
      }

      // Guest (SHARE token) on a non-shared-resource URL (e.g. /property without a resource ID):
      // skip Okta-only APIs (entitlements, user-profile) that would 401 and trigger logout.
      // Signal empty entitlements so MfContainer renders with a guest-specific error. #genai
      if (!isResourceShared && isShareToken) {
        getGuestUserDetails();
        getLoaders();
        return;
      }

      if (isResourceShared && !sharedIsEVUser && sharedHasAccess) {
        // Case 1: Non-EV (guest) user with access — app details + v2 user profile
        fetchSharedAppConfig({ appCode: sharedAppCode, checkEntitlementsFirst: false });
        getGuestUserDetails();
        getLoaders();
        return;
      }

      if (isResourceShared && sharedIsEVUser && sharedHasAccess) {
        // Case 2a: EV user with share access — try old entitlements first, fallback to new API
        fetchSharedAppConfig({ appCode: sharedAppCode, checkEntitlementsFirst: true });
        getUserDetails(true);
        getLoaders();
        return;
      }

      // Case 2b / Normal: not shared, or EV user without share access — existing flow
      getMfaConfigAction();
      getUserDetails(true);
      getLoaders();
    }
  };

  const pollUnreadNotifs = (payload) => dispatch(actionCreators.pollUnreadNotifications(payload));

  const startNotificationsPolling = () => {
    if (userDetails.userName && (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES')) {
      pollUnreadNotifs({ userId: userDetails.userName, monitor: `${(new Date().valueOf())}` });
    }
  };

  useEffect(startNotificationsPolling, [userDetails]);

  useEffect(() => {
    if (featureFlagsLoaded) {
      setShowNewUI(get(flags, 'vpNavbarFullscreen', true));
      setEvoneLdShowBanner(get(flags, 'showHorizonBanner', false));
    }
  }, [featureFlagsLoaded, flags]);

  // Wait for SharedStartupGate
  const entitlementCalledRef = React.useRef(false);
  useEffect(() => {
    if (entitlementCalledRef.current) return;
    if (sharedPhase === ACCESS_PHASES.READY || sharedPhase === ACCESS_PHASES.LEGACY) {
      entitlementCalledRef.current = true;
      getMfaConfigAndUser();
    }
  }, [sharedPhase]);

  // Public routes render without app chrome (header / sidebar / SharedStartupGate).
  // Uses window.location.pathname because the <Router> hasn't mounted yet at this point.
  const { pathname } = window.location;
  const isPublicRoute = PUBLIC_PATHS.includes(pathname) || isAuthorizePath(pathname);

  if (isPublicRoute) {
    return (
      <ErrorBoundary fallback={AppErrorFallback(t)}>
        <Router>
          <Routes />
        </Router>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary fallback={AppErrorFallback(t)}>
      <SharedStartupGate>
        {/* #genai shared startup gate wraps legacy flow */}
        {isAuthorized() && showNewUI && (
          <FullscreenToggleButton
            shouldHeaderRender={shouldHeaderRender}
            toggleFullscreen={toggleFullscreenAction}
            showNewUI={showNewUI}
            mfaList={mfaList}
          />
        )}
        {isAuthorized() && <Header show={showNewUI && shouldHeaderRender} userProfileError={userProfileError} />}
        <div style={(showNewUI && shouldHeaderRender) ? { display: 'flex', maxHeight: 'calc(100vh - 44px)' } : { display: 'flex', maxHeight: '100vh' }}>
          <Router>
            { isAuthorized() && window.location.pathname !== '/start' && (<SideBar />) }
            <Routes />
          </Router>
        </div>
      </SharedStartupGate>
    </ErrorBoundary>
  );
};

/**
 * selects required data from redux store
 * @param {Object} state - redux store object
 */
const selector = (state) => ({
  authorize: state.Authorize.authorize,
  userDetails: state.SideBar.userDetails,
  featureFlagsLoaded: state.SideBar.featureFlagsLoaded,
  showNewUI: state.SideBar.showNewUI,
  shouldHeaderRender: state.SideBar.shouldHeaderRender,
  mfaList: state.SideBar.mfaConfig,
  userProfileError: state.SideBar.userProfileError,
  sharedPhase: state.GuestAccess.sharedPhase,
  // #genai shared-resource branching props (common GuestAccess state)
  isResourceShared: state.GuestAccess.isResourceShared,
  sharedIsEVUser: state.GuestAccess.isEvUser,
  sharedHasAccess: state.GuestAccess.hasAccess,
  sharedAppCode: state.GuestAccess.appCode,
  isShareToken: Boolean(state.Authorize?.isShareToken),
});

/**
 * returns a set of actions after binding dispatch
 * @param {Function} dispatch - redux dispatch function
 */
const actions = (dispatch) => ({
  actions: bindActionCreators({ ...actionCreators, ...authorizeActions }, dispatch),
});

App.propTypes = {
  /**
   * set of actions passed by actions function
   */
  actions: PropTypes.shape({
    getMfaConfigAction: PropTypes.func.isRequired,
    getUserDetails: PropTypes.func.isRequired,
    getGuestUserDetails: PropTypes.func.isRequired,
    getLoaders: PropTypes.func.isRequired,
    navigateAction: PropTypes.func.isRequired,
    setShowNewUI: PropTypes.func.isRequired,
    setEvoneLdShowBanner: PropTypes.func.isRequired,
    toggleFullscreenAction: PropTypes.func.isRequired,
    validateUserImpersonation: PropTypes.func.isRequired,
    fetchSharedAppConfig: PropTypes.func.isRequired,
  }).isRequired,

  /**
   * store value containing the status of authentication
   * @param {Boolean} authorize
   */
  authorize: PropTypes.bool,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape(),

  /**
   * launchdarkly feature flags loaded
   * @property {Boolean} featureFlagsLoaded
   */
  featureFlagsLoaded: PropTypes.bool.isRequired,

  /**
   * launchdarkly feature flags
   * @property {Object} flags
   */
  flags: PropTypes.shape().isRequired,

  /**
   * show new UI
   * @property {Object} flags
   */
  showNewUI: PropTypes.shape().isRequired,

  /**
   * show header
   * @property {Object} flags
   */
  shouldHeaderRender: PropTypes.shape().isRequired,

  /**
   * store value containing the status of user profile error
   * @param {Boolean} userProfileError
   */
  userProfileError: PropTypes.bool,

  sharedPhase: PropTypes.string,
  isResourceShared: PropTypes.bool,
  sharedIsEVUser: PropTypes.bool,
  sharedHasAccess: PropTypes.bool,
  sharedAppCode: PropTypes.string,
  isShareToken: PropTypes.bool,

  /**
   * The list of micro applications.
   * @param {{appId: String, rootUrl: String, prefetch: Boolean, name: String}[]} mfaList
   */
  mfaList: PropTypes.arrayOf(
    PropTypes.shape({
      appId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      rootUrl: PropTypes.string.isRequired,
      prefetch: PropTypes.bool,
    }),
  ),

};

App.defaultProps = {
  authorize: false,
  userDetails: {},
  mfaList: [],
  userProfileError: false,
  sharedPhase: ACCESS_PHASES.LOADING,
  isResourceShared: false,
  sharedIsEVUser: false,
  sharedHasAccess: false,
  sharedAppCode: '',
  isShareToken: false,
};

export { App };

export default connect(selector, actions)(withLDConsumer()(App));
